import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useCallback, useState, VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { Accordion } from "components/Accordion";
import { useAnchor } from "hooks";
import { DataTestId } from "types/enums/testId";
import { FaqContent } from "types/graphql/generated";

interface Props {
  content: FaqContent;
}

const FAQ: VFC<Props> = withErrorBoundary(({ content: { title, questions } }) => {
  const [expanded, setExpanded] = useState<string | undefined>();

  const handleChange = useCallback(
    (panelId: string) => (isExpanded: boolean) => (isExpanded ? setExpanded(panelId) : setExpanded(undefined)),
    []
  );

  useAnchor("faq");

  return (
    <StyledFAQ className="faq">
      <div className="Content">
        <div className="FAQ__container" data-testid={DataTestId.FAQContainer}>
          <h2 className="FAQ__title Font_40_48_600">{title}</h2>
          <div className="FAQ__content">
            {questions?.map(
              (question) =>
                question && (
                  <Accordion
                    title={question.title}
                    key={question.id}
                    expanded={expanded === question.id}
                    onChange={handleChange(question.id)}
                  >
                    <Markdown>{question.answer}</Markdown>
                  </Accordion>
                )
            )}
          </div>
        </div>
      </div>
    </StyledFAQ>
  );
}, {});

const StyledFAQ = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .FAQ__container {
    display: flex;

    /* grid-template-columns: 2fr 3fr; */

    /* grid-column-gap: 104px; */
    justify-content: space-between;
  }

  .FAQ__title {
    max-width: 290px;
    width: 100%;
  }

  .FAQ__content {
    margin-top: -16px;
    margin-left: 104px;
    max-width: 792px;
    width: 100%;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .FAQ__content {
      margin-left: 64px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .FAQ__container {
      flex-direction: column;
    }

    .FAQ__content {
      margin-top: 40px;
      margin-left: unset;
      max-width: unset;
    }
  }
`;

gql`
  fragment FaqContent on ComponentFaqFaq {
    title
    questions {
      id
      title
      answer
    }
  }
`;

export { FAQ };
export type { Props as FAQProps };
